import closeFill from '@iconify/icons-eva/close-fill';
import fileFill from '@iconify/icons-eva/file-fill';
import { Icon } from '@iconify/react';
import { useState } from 'react';
import { Button } from 'src/@/components/ui/button';
import { Card } from 'src/@/components/ui/card';
import { Switch } from 'src/@/components/ui/switch';
import { Label } from 'src/@/components/ui/label';
import { Separator } from 'src/@/components/ui/separator';
import { motion, AnimatePresence } from 'framer-motion';
import { isString } from 'lodash';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';

// utils
import { UploadIllustration } from '../../assets';
import { fData } from '../../utils/formatNumber';
//
import { MIconButton } from '../@material-extend';
import { varFade } from '../animate';
import { verifyFeatures } from 'src/utils/functions';
import { LoadingButton } from 'src/@/components/ui/loading-button';
import { validateInstagramFile, INSTAGRAM_ACCEPT, INSTAGRAM_LIMIT_LABELS } from 'src/utils/instagramMediaLimits';

// ---------------------------------------------------------------------- 

UploadMultiFile.propTypes = {
  error: PropTypes.bool,
  showPreview: PropTypes.bool,
  files: PropTypes.array,
  onRemove: PropTypes.func,
  onRemoveAll: PropTypes.func,
  sx: PropTypes.object
};

export default function UploadMultiFile({
  canal,
  error,
  cliente,
  isStaff,
  isDocument,
  changeIsDocument,
  loadingEnviarArquivos,
  onEnviarArquivos,
  showPreview = true,
  files,
  onRemove,
  onRemoveAll,
  sx,
  ...other }) {
  const hasFile = files.length > 0;
  const [rejectedFiles, setRejectedFiles] = useState([]);

  // Função para validar tamanho de arquivo baseado no tipo e canal
  const validateFileSize = (file) => {
    console.log(canal, file.size, file.type, isDocument);

    // Instagram checa ANTES do atalho "isDocument" (que libera 50MB pra qualquer
    // formato) — senão marcar "enviar como documento" burlaria os limites da Meta.
    if (canal === "instagram") {
      const result = validateInstagramFile(file);
      if (!result.ok) {
        return result.reason === 'format'
          ? { code: 'file-invalid-type', message: 'Formato não suportado pelo Instagram' }
          : { code: 'file-too-large', message: 'Arquivo muito grande para o Instagram' };
      }
      return null;
    }

    // Se está enviando como documento, usar limite de 50MB para todos os arquivos
    if (isDocument) {
      return file.size > 50 * 1024 * 1024 ? { code: 'file-too-large', message: 'Documento muito grande (máximo 50MB)' } : null;
    }

    if (canal === "whatsapp_business") {
      const fileType = file.type;
      const fileSize = file.size;
      
      // Limites específicos do WhatsApp Business
      const limits = {
        'image': 16 * 1024 * 1024, // 16MB
        'video': 16 * 1024 * 1024, // 16MB
        'audio': 16 * 1024 * 1024, // 16MB
        'application': 40 * 1024 * 1024, // 40MB para documentos
        'text': 40 * 1024 * 1024 // 40MB para documentos
      };
      
      if (fileType.startsWith('image/')) {
        return fileSize > limits.image ? { code: 'file-too-large', message: 'Imagem muito grande' } : null;
      } else if (fileType.startsWith('video/')) {
        return fileSize > limits.video ? { code: 'file-too-large', message: 'Vídeo muito grande' } : null;
      } else if (fileType.startsWith('audio/')) {
        return fileSize > limits.audio ? { code: 'file-too-large', message: 'Áudio muito grande' } : null;
      } else if (fileType.startsWith('application/') || fileType.startsWith('text/')) {
        return fileSize > limits.application ? { code: 'file-too-large', message: 'Documento muito grande' } : null;
      }
    }
    
    // Para outros canais, usar limites padrão
    return file.size > 40000000 ? { code: 'file-too-large', message: 'Arquivo muito grande' } : null;
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    // Instagram: sem maxSize fixo aqui — validateFileSize já cobre o limite por
    // tipo (imagem 8MB, video/audio/pdf 25MB) via validateInstagramFile.
    maxSize: canal === "instagram" ? undefined : 40000000,
    accept: canal === "instagram" ? INSTAGRAM_ACCEPT : ['image/*', 'application/*', 'audio/*', 'video/*', 'text/*'],
    validator: validateFileSize,
    ...other,

  });

  const ShowRejectionItems = () => {
    const getErrorMessage = (file) => {
      if (canal === "whatsapp_business") {
        const fileType = file.type;
        if (fileType.startsWith('image/')) {
          return "Imagem muito grande. Máximo permitido: 16MB";
        } else if (fileType.startsWith('video/')) {
          return "Vídeo muito grande. Máximo permitido: 16MB";
        } else if (fileType.startsWith('audio/')) {
          return "Áudio muito grande. Máximo permitido: 16MB";
        } else if (fileType.startsWith('application/') || fileType.startsWith('text/')) {
          return "Documento muito grande. Máximo permitido: 40MB";
        }
      } else if (canal === "instagram") {
        const result = validateInstagramFile(file);
        if (result.reason === 'format') {
          return "Formato não suportado pelo Instagram. Aceitos: imagem (PNG/JPEG), vídeo (MP4/MOV/WEBM/AVI/OGG), áudio (AAC/M4A/WAV) e PDF";
        }
        const kind = result.kind;
        const label = kind ? INSTAGRAM_LIMIT_LABELS[kind] : null;
        return label
          ? `Arquivo muito grande. Máximo permitido para ${kind}: ${label.size}`
          : "Arquivo muito grande para o Instagram";
      }
      return "Arquivo muito grande. Máximo permitido: 40MB";
    };

    const handleSendAsDocument = () => {
      changeIsDocument(true);
    };

    return (
      <Card className="py-2 px-4 mt-6 border-red-300 bg-red-50">
        {fileRejections.map(({ file, errors }) => {
          const { path } = file;
          return (
            <div key={path} className="my-2">
              <h6 className="text-sm font-semibold truncate">
                {path}
              </h6>
              {errors.map((e) => (
                <p key={e.code} className="text-xs text-gray-600">
                  - {getErrorMessage(file)}
                </p>
              ))}
              {/* Instagram não tem atalho "enviar como documento" — a Meta só aceita PDF */}
              {canal !== "instagram" && (
                <p className="text-xs mt-2 text-blue-600">
                  💡 Dica: Ative "Enviar como documento" para enviar arquivos maiores (até 50MB)
                </p>
              )}
            </div>
          );
        })}
      </Card>
    );
  };


  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={`
          outline-none flex text-center items-center flex-col justify-center p-8 rounded-lg bg-neutral-50 border-2 border-dashed border-gray-300 hover:opacity-72 cursor-pointer
          md:text-left md:flex-row
          ${isDragActive ? 'opacity-72' : ''}
          ${(isDragReject || error) ? 'text-red-500 border-red-300 bg-red-50' : ''}
        `}
      >
        <input {...getInputProps()} />

        <UploadIllustration className="w-56" />

        <div className="p-6 md:ml-4">
          <h5 className="text-xl font-semibold mb-2">
            Arraste ou selecione
          </h5>

          <p className="text-sm text-gray-600">
            Arraste arquivos ou selecione&nbsp;
            <span className="text-blue-600 underline">
              buscando
            </span>
            &nbsp;em sua máquina
          </p>
        </div>
      </div>

      {fileRejections.length > 0 && <ShowRejectionItems />}

      <div className={hasFile ? 'my-6' : ''}>
        <AnimatePresence>
          {files.map((file) => {
            const { name, size, preview } = file;
            const key = isString(file) ? file : name;

            if (showPreview) {
              return (
                <motion.div
                  key={key}
                  {...varFade().inRight}
                  className="p-0 m-1 w-20 h-20 rounded-xl overflow-hidden relative inline-flex"
                >
                  <img
                    src={isString(file) ? file : preview}
                    className="w-full h-full object-cover absolute"
                    alt="Preview"
                  />
                  <div className="absolute top-1.5 right-1.5">
                    <MIconButton
                      size="small"
                      onClick={() => onRemove(file)}
                      className="p-0.5 text-white bg-black bg-opacity-72 hover:bg-opacity-48"
                    >
                      <Icon icon={closeFill} />
                    </MIconButton>
                  </div>
                </motion.div>
              );
            }

            return (
              <motion.div
                key={key}
                {...varFade().inRight}
                className="my-2 py-3 px-4 rounded-lg border border-gray-200 bg-white flex items-center"
              >
                <div className="mr-3">
                  <Icon icon={fileFill} width={28} height={28} />
                </div>
                <div className="flex-1">
                  <h6 className="text-sm font-semibold">
                    {isString(file) ? file : name}
                  </h6>
                  <p className="text-xs text-gray-500">
                    {isString(file) ? '' : fData(size)}
                  </p>
                </div>
                <div>
                  <MIconButton size="small" onClick={() => onRemove(file)}>
                    <Icon icon={closeFill} />
                  </MIconButton>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>


        {/* Instagram: a Meta so aceita PDF como documento (ja incluso nos limites
            fixos acima) — o backend nem le esse campo pra instagram, entao o
            switch nao tem efeito nenhum nesse canal. */}
        {canal !== "instagram" && (
          <div className="flex items-center space-x-2 mt-6">
            <Switch
              checked={isDocument}
              onCheckedChange={(checked) => {
                changeIsDocument(checked);
              }}
              id="document-mode"
            />
            <Label htmlFor="document-mode" className="flex flex-col">
              <span className="text-sm">Enviar como documento</span>
              <span className="text-xs text-gray-500">
                {isDocument
                  ? "Limite: 40MB para todos os arquivos"
                  : canal === "whatsapp_business"
                    ? "Limite: 5MB (imagens), 16MB (vídeo), 16MB (áudio), 40MB (documentos)"
                    : "Limite: 16MB (vídeo/áudio/imagens)"
                }
              </span>
            </Label>
          </div>
        )}



      <Separator className="my-4 border-dashed" />

      {hasFile && (
        <div className="flex justify-end space-x-3">
          <Button onClick={onRemoveAll} variant="outline">
            Remover Todos
          </Button>
          <LoadingButton onClick={onEnviarArquivos} loading={loadingEnviarArquivos}>
            Enviar arquivos
          </LoadingButton>
        </div>
      )}
    </div>
  );
}
